import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { adminApi, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

const day = (iso) => new Date(iso).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" });

export const PhotoCleanup = () => {
  const [p, setP] = useState(null);
  const [last, setLast] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => adminApi().get("/admin/cleanup/last").then((r) => setLast(r.data.at ? r.data : null)).catch(() => {});
  useEffect(() => { load(); }, []);
  const check = async () => {
    try { const { data } = await adminApi().get("/admin/cleanup/preview"); setP(data); setOpen(true); } catch (e) { toast.error(errMsg(e)); }
  };
  const run = async () => {
    setBusy(true);
    try { const { data } = await adminApi().post("/admin/cleanup/run"); toast.success(`Deleted ${data.files} photos/files and ${data.drafts} old drafts`); setOpen(false); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const nothing = p && !p.files && !p.drafts && !p.orders;
  return (
    <section className="mt-6 flex flex-wrap items-center justify-between gap-3 border-2 border-slate-200 bg-white p-4" data-testid="photo-cleanup">
      <div>
        <div className="font-display text-base font-black uppercase">Photo clean-up</div>
        <p className="text-xs text-slate-600">Delete customer photos and designs older than 3 months, as promised in the privacy policy. Order records are kept.</p>
        {last && <p className="mt-1 text-[11px] font-bold text-slate-500" data-testid="photo-cleanup-last">Last run {day(last.at)}: {last.files} files, {last.drafts} drafts</p>}
      </div>
      <button type="button" onClick={check} className="flex items-center gap-2 border-2 border-red-600 px-4 py-2 text-xs font-black uppercase text-red-600 hover:bg-red-50" data-testid="photo-cleanup-button"><Trash2 className="h-4 w-4" /> Clean up old photos</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="photo-cleanup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">Delete photos older than 3 months?</DialogTitle>
            <DialogDescription>Everything from before {p && day(p.cutoff)}. This can't be undone.</DialogDescription>
          </DialogHeader>
          {p && (nothing ? <p className="text-sm font-bold" data-testid="photo-cleanup-nothing">Nothing to delete – everything is less than 3 months old.</p> : (
            <ul className="space-y-1 text-sm" data-testid="photo-cleanup-summary">
              <li><strong>{p.files}</strong> photos, crests and print files will be deleted</li>
              <li><strong>{p.drafts}</strong> old designs and drafts will be deleted</li>
              <li><strong>{p.orders}</strong> orders will have their photos removed (names, sizes and totals are kept)</li>
            </ul>
          ))}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="border-2 border-slate-900 px-4 py-2 text-sm font-bold" data-testid="photo-cleanup-cancel">Cancel</button>
            {!nothing && <button type="button" onClick={run} disabled={busy} className="flex items-center gap-2 bg-red-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-60" data-testid="photo-cleanup-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Delete now</button>}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
